import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

import PageHeader from "pages/components/PageHeader";
import ProjectTable from "pages/components/ProjectTable/ProjectTable";
import DialogBarcodeReader from "pages/components/DialogBarcodeReader";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Fab from "@mui/material/Fab";
import Autocomplete from "@mui/material/Autocomplete";

import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";

import MDButton from "components/MDButton";

export default function InventarioDetalhe() {
  const { idinventario } = useParams();
  const { userData } = useAuth();
  const { showAlert } = useAlert();

  const idutilizador = Number(userData?.id || 0);

  const [loading, setLoading] = useState(false);
  const [inventario, setInventario] = useState(null);
  const [reloadInventario, setReloadInventario] = useState(0);

  const [materialContagem, setMaterialContagem] = useState(null);
  const [associacaoEANContagem, setAssociacaoEANContagem] = useState(null);
  const [quantidadeContagem, setQuantidadeContagem] = useState("");

  // Quantidade a recuperar após escolher outra associação para o mesmo EAN.
  const [quantidadePendenteEAN, setQuantidadePendenteEAN] = useState(null);

  // Código de barras a reutilizar na escolha de outra associação.
  const [codigoPendenteEAN, setCodigoPendenteEAN] = useState(null);

  const [localizacaoContagem, setLocalizacaoContagem] = useState("");
  const [observacoesContagem, setObservacoesContagem] = useState("");

  const [guardarLoading, setGuardarLoading] = useState(false);
  const [reloadMateriais, setReloadMateriais] = useState(0);

  // Anulação de todas as contagens válidas de um material.
  const [materialAnular, setMaterialAnular] = useState(null);
  const [motivoAnulacao, setMotivoAnulacao] = useState("");
  const [anularLoading, setAnularLoading] = useState(false);

  // Contagem anterior encontrada para o mesmo material/localização.
  const [confirmacaoContagem, setConfirmacaoContagem] = useState(null);

  const [tipoConfirmacao, setTipoConfirmacao] = useState("");
  const [quantidadeRecontagem, setQuantidadeRecontagem] = useState("");
  const [observacoesConfirmacao, setObservacoesConfirmacao] = useState("");

  const [openBarcodeReader, setOpenBarcodeReader] = useState(false);

  const [usarCameraBarcode, setUsarCameraBarcode] = useState(true);

  const [loadingMateriais, setLoadingMateriais] = useState(false);

  // Pesquisa de materiais para inventários LIVRE.
  const [termoPesquisaMaterial, setTermoPesquisaMaterial] = useState("");
  const [materiaisPesquisa, setMateriaisPesquisa] = useState([]);
  const [loadingPesquisaMaterial, setLoadingPesquisaMaterial] = useState(false);

  const limparPesquisaMaterial = () => {
    setTermoPesquisaMaterial("");
    setMateriaisPesquisa([]);
  };

  const [dadosMateriais, setDadosMateriais] = useState({
    total: 0,
    total_por_contar: 0,
    total_contados: 0,
    por_contar: [],
    contados: [],
  });

  // Atualizar o estado quando o utilizador regressa a esta janela.
  useEffect(() => {
    const atualizarAoFocar = () => {
      if (document.visibilityState === "visible") {
        setReloadInventario((anterior) => anterior + 1);
      }
    };

    window.addEventListener("focus", atualizarAoFocar);

    return () => {
      window.removeEventListener("focus", atualizarAoFocar);
    };
  }, []);

  useEffect(() => {
    if (!idutilizador || !idinventario) return;

    let ativo = true;

    const carregarInventario = async () => {
      setLoading(true);

      const [data, err] = await handleApi(
        api.post("Stocks/SelInventario", {
          idinventario: Number(idinventario),
          idutilizador,
        })
      );

      if (!ativo) return;

      setLoading(false);

      if (err) {
        showAlert(err, "error");
        setInventario(null);
        return;
      }

      setInventario(data || null);
    };

    carregarInventario();

    return () => {
      ativo = false;
    };
  }, [idinventario, idutilizador, reloadInventario]);

  useEffect(() => {
    if (!idutilizador || !idinventario || !inventario) return;

    let ativo = true;

    const carregarMateriais = async () => {
      setLoadingMateriais(true);

      const [data, err] = await handleApi(
        api.post("Stocks/SelInventarioMateriais", {
          idinventario: Number(idinventario),
          idutilizador,
        })
      );

      if (!ativo) return;

      setLoadingMateriais(false);

      if (err) {
        showAlert(err, "error");
        return;
      }

      setDadosMateriais({
        total: Number(data?.total || 0),
        total_por_contar: Number(data?.total_por_contar || 0),
        total_contados: Number(data?.total_contados || 0),
        por_contar: Array.isArray(data?.por_contar) ? data.por_contar : [],
        contados: Array.isArray(data?.contados) ? data.contados : [],
      });
    };

    carregarMateriais();

    return () => {
      ativo = false;
    };
  }, [idinventario, idutilizador, inventario, reloadMateriais]);

  // Pesquisa de qualquer material ativo num inventário LIVRE.
  useEffect(() => {
    if (
      !idutilizador ||
      !idinventario ||
      inventario?.modo !== "LIVRE" ||
      inventario?.dataencerramento ||
      termoPesquisaMaterial.trim().length < 2
    ) {
      setMateriaisPesquisa([]);
      setLoadingPesquisaMaterial(false);
      return;
    }

    let ativo = true;

    const timer = setTimeout(async () => {
      setLoadingPesquisaMaterial(true);

      try {
        const [data, err, meta] = await handleApi(
          api.post("Stocks/SelInventarioMateriaisPesquisa", {
            idinventario: Number(idinventario),
            idutilizador,
            term: termoPesquisaMaterial.trim(),
          })
        );

        if (!ativo) return;

        if (err || meta?.success === false) {
          setMateriaisPesquisa([]);
          showAlert(err || meta?.message || "Erro ao pesquisar materiais.", "error");
          return;
        }

        setMateriaisPesquisa(Array.isArray(data) ? data : []);
      } catch (error) {
        if (!ativo) return;

        setMateriaisPesquisa([]);
        showAlert(error?.message || "Erro ao pesquisar materiais.", "error");
      } finally {
        if (ativo) setLoadingPesquisaMaterial(false);
      }
    }, 350);

    return () => {
      ativo = false;
      clearTimeout(timer);
    };
  }, [
    idinventario,
    idutilizador,
    inventario?.modo,
    inventario?.dataencerramento,
    termoPesquisaMaterial,
  ]);

  const abrirContagem = (material, associacaoEAN = null) => {
    if (!material) return;

    setMaterialContagem(material);
    setAssociacaoEANContagem(associacaoEAN);

    // Cada operação acrescenta uma nova contagem.
    // Nunca preencher com o total anteriormente contado.
    setQuantidadeContagem("");

    // A localização pertence a esta contagem individual.
    // Não assumir automaticamente a localização antiga do material.
    setLocalizacaoContagem("");
    setObservacoesContagem("");
  };

  const conversaoEANContagem = (() => {
    if (!associacaoEANContagem) return null;

    const tipo = String(associacaoEANContagem.tipo_conversao || "").toUpperCase();
    const unidades = Number(associacaoEANContagem.unidades_conversao);

    let numerador = 1;
    let denominador = 1;

    if (tipo === "B" && Number.isSafeInteger(unidades) && unidades > 1) {
      denominador = unidades;
    } else if (tipo === "C" && Number.isSafeInteger(unidades) && unidades > 1) {
      numerador = unidades;
    } else if (tipo !== "A") {
      const fator = Number(associacaoEANContagem.quantidade);

      if (!Number.isFinite(fator) || fator <= 0) return null;

      numerador = fator;
    }

    const quantidade = Number(String(quantidadeContagem).replace(",", "."));

    return {
      codigo: String(associacaoEANContagem.codigo || ""),
      numerador,
      denominador,
      fator: numerador / denominador,
      quantidadeConvertida:
        quantidadeContagem !== "" && Number.isFinite(quantidade)
          ? (quantidade * numerador) / denominador
          : null,
    };
  })();

  const converterQuantidadeParaStock = (quantidade) => {
    const valor = Number(quantidade);

    if (!Number.isFinite(valor)) return 0;

    if (!associacaoEANContagem) return valor;

    if (!conversaoEANContagem) return 0;

    return (valor * conversaoEANContagem.numerador) / conversaoEANContagem.denominador;
  };

  const formatarQuantidadeStock = (quantidade) =>
    Number(quantidade).toLocaleString("pt-PT", {
      maximumFractionDigits: 12,
    });

  const guardarContagem = async () => {
    if (!materialContagem || guardarLoading) return;

    if (inventario?.dataencerramento) {
      showAlert("Este inventário já está encerrado.", "warning");
      return;
    }

    const textoQuantidade = String(quantidadeContagem).trim().replace(",", ".");

    if (!textoQuantidade) {
      showAlert("Indica a quantidade contada.", "warning");
      return;
    }

    const quantidade = Number(textoQuantidade);

    if (
      !Number.isFinite(quantidade) ||
      quantidade < 0 ||
      quantidade >= 1000000000000 ||
      !/^\d+(\.\d{1,6})?$/.test(textoQuantidade)
    ) {
      showAlert("Indica uma quantidade válida, com até 6 casas decimais.", "warning");
      return;
    }

    if (associacaoEANContagem && !conversaoEANContagem) {
      showAlert(
        "Não foi possível determinar a conversão do código EAN. A contagem não será gravada.",
        "error"
      );
      return;
    }

    setGuardarLoading(true);

    try {
      // Consultar a última contagem válida do mesmo material
      // e localização antes de tentar gravar.
      const [ultimaData, ultimaErr, ultimaMeta] = await handleApi(
        api.post("Stocks/SelInventarioUltimaContagem", {
          idinventario: Number(idinventario),
          refmaterial: materialContagem.refmaterial,
          localizacao: localizacaoContagem.trim() || null,
          idutilizador,
        })
      );

      if (ultimaErr || ultimaMeta?.success === false) {
        showAlert(
          ultimaErr || ultimaMeta?.message || "Erro ao consultar a última contagem.",
          "error"
        );
        return;
      }

      if (!ultimaData || typeof ultimaData.existe !== "boolean") {
        showAlert("A API devolveu uma resposta inválida.", "error");
        return;
      }

      // Já existe uma contagem nesta localização.
      // Não gravar outra sem uma escolha explícita do utilizador.
      if (ultimaData.existe) {
        if (!ultimaData.contagem?.id) {
          showAlert("Não foi possível identificar a contagem anterior.", "error");
          return;
        }

        setConfirmacaoContagem({
          anterior: ultimaData.contagem,
          quantidadeNova: quantidade,
        });

        showAlert(
          "Já existe uma contagem deste material. É necessário indicar se pretende substituir, acrescentar ou recontar.",
          "warning"
        );
        return;
      }

      // Primeira contagem nesta localização: registo direto.
      const [data, err, meta] = await handleApi(
        api.post("Stocks/InsInventarioContagem", {
          idinventario: Number(idinventario),
          refmaterial: materialContagem.refmaterial,
          origem: associacaoEANContagem ? "EAN" : "MANUAL",
          codigo_ean: associacaoEANContagem ? conversaoEANContagem?.codigo : null,
          localizacao: localizacaoContagem.trim(),
          quantidade_lida: quantidade,
          tipo_operacao: "INICIAL",
          idcontagem_anterior: null,
          observacoes: null,
          fator_numerador: conversaoEANContagem?.numerador ?? 1,
          fator_denominador: conversaoEANContagem?.denominador ?? 1,
          idutilizador,
        })
      );

      // O handleApi pode devolver err=null mesmo quando
      // o backend responde com success=false.
      if (err || meta?.success === false) {
        showAlert(err || meta?.message || "Erro ao registar a contagem.", "error");
        return;
      }

      if (!data?.id) {
        showAlert("A API não confirmou o registo da contagem.", "error");
        return;
      }

      concluirContagemEAN();
      setReloadMateriais((anterior) => anterior + 1);

      showAlert(meta?.message || "Contagem registada com sucesso.", "success");
    } catch (error) {
      showAlert(error?.message || "Erro ao guardar a contagem.", "error");
    } finally {
      setGuardarLoading(false);
    }
  };

  const confirmarContagemRepetida = async () => {
    if (!confirmacaoContagem || !materialContagem || guardarLoading) return;

    if (!["SUBSTITUIR", "ACRESCENTAR", "RECONTAR"].includes(tipoConfirmacao)) {
      showAlert("Seleciona uma operação.", "warning");
      return;
    }

    const textoQuantidade =
      tipoConfirmacao === "RECONTAR"
        ? String(quantidadeRecontagem).trim().replace(",", ".")
        : String(confirmacaoContagem.quantidadeNova);

    if (
      !/^\d+(\.\d{1,6})?$/.test(textoQuantidade) ||
      !Number.isFinite(Number(textoQuantidade)) ||
      Number(textoQuantidade) >= 1000000000000
    ) {
      showAlert("Indica uma quantidade válida, até 6 casas decimais.", "warning");
      return;
    }

    const quantidade = Number(textoQuantidade);

    if (associacaoEANContagem && !conversaoEANContagem) {
      showAlert(
        "Não foi possível determinar a conversão do código EAN. A contagem não será gravada.",
        "error"
      );
      return;
    }

    setGuardarLoading(true);

    try {
      const [data, err, meta] = await handleApi(
        api.post("Stocks/InsInventarioContagem", {
          idinventario: Number(idinventario),
          refmaterial: materialContagem.refmaterial,
          origem: associacaoEANContagem ? "EAN" : "MANUAL",
          codigo_ean: associacaoEANContagem ? conversaoEANContagem?.codigo : null,
          localizacao: localizacaoContagem.trim() || null,
          quantidade_lida: quantidade,
          fator_numerador: conversaoEANContagem?.numerador ?? 1,
          fator_denominador: conversaoEANContagem?.denominador ?? 1,
          tipo_operacao: tipoConfirmacao,
          idcontagem_anterior: confirmacaoContagem.anterior.id,
          observacoes: observacoesConfirmacao.trim() || null,
          idutilizador,
        })
      );

      if (err || meta?.success === false || !data?.id) {
        showAlert(err || meta?.message || "Não foi possível registar a contagem.", "error");

        // A contagem anterior pode ter sido atualizada por outro funcionário.
        // Não reutilizar a confirmação desatualizada.
        setConfirmacaoContagem(null);
        setTipoConfirmacao("");
        setReloadMateriais((anterior) => anterior + 1);
        return;
      }

      concluirContagemEAN();
      setReloadMateriais((anterior) => anterior + 1);

      showAlert(`Contagem registada. Quantidade final: ${data.quantidade_final}.`, "success");
    } catch (error) {
      showAlert(error?.message || "Erro ao guardar a contagem.", "error");
    } finally {
      setGuardarLoading(false);
    }
  };

  const anularContagensMaterial = async () => {
    if (!materialAnular || anularLoading) return;

    if (inventario?.dataencerramento) {
      showAlert("Não é possível anular contagens de um inventário encerrado.", "warning");
      return;
    }

    const motivo = motivoAnulacao.trim();

    if (!motivo) {
      showAlert("Indica o motivo da anulação.", "warning");
      return;
    }

    setAnularLoading(true);

    try {
      const [data, err, meta] = await handleApi(
        api.post("Stocks/CancelarInventarioMaterial", {
          idinventario: Number(idinventario),
          refmaterial: materialAnular.refmaterial,
          idutilizador,
          observacoes_cancelamento: motivo,
        })
      );

      if (err || meta?.success === false || !data?.total_anuladas) {
        showAlert(err || meta?.message || "Não foi possível anular as contagens.", "error");
        return;
      }

      setMaterialAnular(null);
      setMotivoAnulacao("");

      setReloadMateriais((anterior) => anterior + 1);

      showAlert(meta?.message || "Contagens anuladas com sucesso.", "success");
    } catch (error) {
      showAlert(error?.message || "Erro ao anular as contagens.", "error");
    } finally {
      setAnularLoading(false);
    }
  };

  const alterarAssociacaoEAN = () => {
    if (!associacaoEANContagem?.codigo || guardarLoading) return;

    // Guardar o EAN e a quantidade introduzida.
    setCodigoPendenteEAN(String(associacaoEANContagem.codigo).trim());
    setQuantidadePendenteEAN(quantidadeContagem);

    // Fechar a contagem atual, sem gravar.
    fecharContagem();

    // Voltar ao leitor para selecionar outra associação.
    setOpenBarcodeReader(true);
  };

  const concluirContagemEAN = () => {
    const continuarLeitura = Boolean(associacaoEANContagem);

    fecharContagem();

    if (continuarLeitura) {
      setOpenBarcodeReader(true);
    }
  };

  const fecharContagem = () => {
    setMaterialContagem(null);
    setAssociacaoEANContagem(null);
    setQuantidadeContagem("");
    setLocalizacaoContagem("");
    setObservacoesContagem("");
    setConfirmacaoContagem(null);
    setTipoConfirmacao("");
    setQuantidadeRecontagem("");
    setObservacoesConfirmacao("");
  };

  const materiais = useMemo(() => {
    const lista = [...dadosMateriais.por_contar, ...dadosMateriais.contados];

    return lista.sort((a, b) => {
      const dataA = a?.data_registo ? new Date(a.data_registo).getTime() : 0;
      const dataB = b?.data_registo ? new Date(b.data_registo).getTime() : 0;

      return dataB - dataA;
    });
  }, [dadosMateriais]);

  const selecionarMaterialPorCodigo = async (associacao) => {
    const refmaterial = String(associacao?.refmaterial || "").trim();

    if (!refmaterial) {
      showAlert("O código lido não tem uma referência de material válida.", "warning");
      return;
    }

    let material = materiais.find((item) => String(item.refmaterial || "").trim() === refmaterial);

    if (!material && inventario?.modo === "LIVRE") {
      const [data, err, meta] = await handleApi(
        api.post("Stocks/SelInventarioMateriaisPesquisa", {
          idinventario: Number(idinventario),
          idutilizador,
          term: refmaterial,
        })
      );

      if (err || meta?.success === false) {
        showAlert(err || meta?.message || "Erro ao pesquisar o material.", "error");
        return;
      }

      material = (Array.isArray(data) ? data : []).find(
        (item) => String(item.refmaterial || "").trim() === refmaterial
      );
    }

    if (!material) {
      showAlert(`O material ${refmaterial} não pertence a este inventário.`, "warning");
      return;
    }

    if (inventario?.dataencerramento) {
      showAlert("Este inventário já está encerrado.", "warning");
      return;
    }

    abrirContagem(material, associacao);

    if (codigoPendenteEAN) {
      setQuantidadeContagem(quantidadePendenteEAN ?? "");
      setCodigoPendenteEAN(null);
      setQuantidadePendenteEAN(null);
    }
  };

  const mostrarQtdOficial = materiais.some(
    (material) => material.qtd_esperada !== null && material.qtd_esperada !== undefined
  );

  const colunasMateriais = useMemo(() => {
    const colunas = [
      {
        Header: "Referência",
        accessor: "refmaterial",
        width: 125,
      },
      {
        Header: "Material",
        accessor: "material",
        width: 360,
        minWidth: 220,
        Cell: ({ value }) => (
          <MDTypography variant="button" sx={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>
            {value || "—"}
          </MDTypography>
        ),
      },

      {
        Header: "Qtd. contada",
        accessor: "qtd_verificada",
        width: 125,
        align: "right",
        sortType: "numPt",
        Cell: ({ value }) => value ?? "—",
      },
      {
        Header: "Unidade",
        accessor: "unidade",
        width: 95,
        Cell: ({ value }) => value || "—",
      },
    ];

    if (mostrarQtdOficial) {
      colunas.push(
        {
          Header: "Qtd. oficial",
          accessor: "qtd_esperada",
          width: 125,
          align: "right",
          sortType: "numPt",
          Cell: ({ value }) => value ?? "—",
        },
        {
          Header: "Diferença",
          accessor: "diferenca",
          width: 110,
          align: "right",
          sortType: "numPt",
          Cell: ({ value }) => value ?? "—",
        }
      );
    }

    if (inventario?.modo !== "LIVRE") {
      colunas.push({
        Header: "Estado",
        accessor: "contado",
        width: 125,
        filterType: "selectExact",
        filterOptions: [
          { value: "0", label: "Por contar" },
          { value: "1", label: "Contado" },
        ],
        Cell: ({ value }) => (
          <MDTypography
            variant="caption"
            fontWeight="bold"
            color={Number(value) === 1 ? "success" : "text"}
          >
            {Number(value) === 1 ? "Contado" : "Por contar"}
          </MDTypography>
        ),
      });
    }

    colunas.push(
      {
        Header: "Local principal",
        accessor: "local_base",
        width: 130,
        Cell: ({ value }) => value || "—",
      },
      {
        Header: "Outros locais",
        accessor: "locais_alt",
        width: 160,
        Cell: ({ value }) => value || "—",
      },
      {
        Header: "Data/Hora contagem",
        accessor: "data_registo",
        width: 165,
        Cell: ({ value }) => {
          if (!value) return "—";

          const data = new Date(value);

          if (Number.isNaN(data.getTime())) return "—";

          return data.toLocaleString("pt-PT", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
        },
      }
    );

    return colunas;
  }, [mostrarQtdOficial, inventario?.modo]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PageHeader />

      <MDBox mt={2}>
        <Card>
          <MDBox p={{ xs: 2, md: 3 }}>
            {loading && (
              <MDBox py={2} display="flex" justifyContent="center">
                <CircularProgress size={28} />
              </MDBox>
            )}

            {!loading && !inventario && (
              <MDTypography variant="body2" color="text">
                Não foi possível carregar o inventário.
              </MDTypography>
            )}

            {!loading && inventario && (
              <>
                <MDTypography variant="h6">Detalhe do inventário</MDTypography>

                <MDTypography
                  variant="body2"
                  color="text"
                  sx={{ mt: 0.5, overflowWrap: "anywhere" }}
                >
                  {inventario.nome}
                </MDTypography>

                <MDBox
                  mt={2}
                  display="grid"
                  gap={2}
                  sx={{
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                      lg: "repeat(4, minmax(0, 1fr))",
                    },
                  }}
                >
                  {[
                    ["Centro", inventario.centro],
                    ["Fabricante", inventario.fabricante],
                    ["Marca", inventario.marca],
                    ["Estado", inventario.dataencerramento ? "Encerrado" : "Aberto"],
                  ].map(([label, value]) => (
                    <MDBox key={label} sx={{ minWidth: 0 }}>
                      <MDTypography variant="caption" color="text" display="block">
                        {label}
                      </MDTypography>

                      <MDTypography
                        variant="button"
                        display="block"
                        sx={{ overflowWrap: "anywhere" }}
                      >
                        {value || "—"}
                      </MDTypography>
                    </MDBox>
                  ))}
                </MDBox>
              </>
            )}
          </MDBox>
        </Card>
      </MDBox>

      {inventario && (
        <MDBox mt={3}>
          <MDBox
            mb={2}
            display="grid"
            gap={2}
            sx={{
              gridTemplateColumns: {
                xs: "repeat(3, minmax(0, 1fr))",
              },
            }}
          >
            {[
              ["Total", dadosMateriais.total],
              ["Por contar", dadosMateriais.total_por_contar],
              ["Contados", dadosMateriais.total_contados],
            ].map(([titulo, valor]) => (
              <Card key={titulo}>
                <MDBox p={{ xs: 1.5, md: 2 }} textAlign="center">
                  <MDTypography
                    variant="subtitle1"
                    color="text"
                    fontWeight="bold"
                    sx={{ fontSize: { xs: "0.95rem", md: "1.1rem" } }}
                  >
                    {titulo}
                  </MDTypography>

                  <MDTypography variant="h5" fontWeight="bold">
                    {loadingMateriais ? "…" : valor}
                  </MDTypography>
                </MDBox>
              </Card>
            ))}
          </MDBox>

          {inventario.modo === "LIVRE" && !inventario.dataencerramento && (
            <Card sx={{ mb: 2 }}>
              <MDBox p={{ xs: 2, md: 3 }}>
                <MDTypography variant="h6" mb={2}>
                  Adicionar material à contagem
                </MDTypography>

                <Autocomplete
                  disableClearable
                  options={materiaisPesquisa}
                  loading={loadingPesquisaMaterial}
                  filterOptions={(options) => options}
                  value={null}
                  inputValue={termoPesquisaMaterial}
                  onInputChange={(_, novoValor, motivo) => {
                    if (motivo === "input" || motivo === "clear") {
                      setTermoPesquisaMaterial(novoValor);
                    }
                  }}
                  onChange={(_, material) => {
                    if (!material) return;

                    abrirContagem(material);
                    setTermoPesquisaMaterial("");
                    setMateriaisPesquisa([]);
                  }}
                  getOptionLabel={(option) => `${option.refmaterial} — ${option.material || ""}`}
                  isOptionEqualToValue={(option, value) =>
                    String(option.refmaterial) === String(value.refmaterial)
                  }
                  noOptionsText={
                    termoPesquisaMaterial.trim().length < 2
                      ? "Escreve pelo menos 2 caracteres."
                      : "Nenhum material encontrado."
                  }
                  loadingText="A pesquisar materiais..."
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Pesquisar material"
                      placeholder="Referência ou descrição..."
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingPesquisaMaterial && (
                              <CircularProgress color="inherit" size={18} />
                            )}

                            {Boolean(termoPesquisaMaterial) && (
                              <IconButton
                                size="small"
                                title="Limpar pesquisa"
                                aria-label="Limpar pesquisa"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  limparPesquisaMaterial();
                                }}
                                sx={{
                                  minWidth: 40,
                                  minHeight: 40,
                                  flexShrink: 0,
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}

                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.refmaterial}>
                      <MDBox sx={{ minWidth: 0 }}>
                        <MDTypography variant="button" fontWeight="bold" display="block">
                          {option.refmaterial}
                        </MDTypography>
                        <MDTypography
                          variant="caption"
                          color="text"
                          display="block"
                          sx={{ whiteSpace: "normal", overflowWrap: "anywhere" }}
                        >
                          {option.material}
                        </MDTypography>
                      </MDBox>
                    </li>
                  )}
                  sx={{ maxWidth: 750 }}
                />

                <MDTypography variant="caption" color="text" display="block" mt={1}>
                  Pesquisa qualquer material ativo. Ao selecioná-lo, abre-se o diálogo para registar
                  a quantidade encontrada.
                </MDTypography>
              </MDBox>
            </Card>
          )}

          <ProjectTable
            tableKey={`inventario-materiais-${idinventario}`}
            title="Materiais do inventário"
            subtitle="Consulta das referências e do estado das contagens"
            rowActions={[
              {
                id: "contar",
                icon: <Inventory2OutlinedIcon fontSize="small" />,
                tooltip: inventario.dataencerramento ? "Inventário encerrado" : "Registar contagem",
                color: "info",
                disabled: Boolean(inventario.dataencerramento),
                onClick: abrirContagem,
              },
              {
                id: "anular",
                icon: <DeleteOutlineIcon fontSize="small" />,
                tooltip: "Anular contagens deste material",
                color: "error",
                disabled: Boolean(inventario.dataencerramento),
                visible: (row) => Number(row.contado) === 1,
                onClick: (row) => {
                  setMaterialAnular(row);
                  setMotivoAnulacao("");
                },
              },
            ]}
            columns={colunasMateriais}
            data={materiais}
            loading={loadingMateriais}
            emptyMessage="Não foram encontrados materiais para este inventário."
            minWidth={950}
            maxHeight="65vh"
            showKeepFilters
          />
        </MDBox>
      )}

      <Dialog
        open={Boolean(materialAnular)}
        onClose={() => {
          if (anularLoading) return;
          setMaterialAnular(null);
          setMotivoAnulacao("");
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            mx: { xs: 1, sm: 2 },
            width: { xs: "calc(100% - 16px)", sm: "100%" },
            maxHeight: "calc(100dvh - 32px)",
          },
        }}
      >
        <DialogTitle>Anular contagens do material</DialogTitle>

        <DialogContent dividers>
          <MDBox display="grid" gap={2}>
            <MDTypography variant="body2">
              Esta operação irá anular todas as contagens válidas do material neste inventário,
              mantendo o histórico.
            </MDTypography>

            <MDBox>
              <MDTypography variant="button" fontWeight="bold" display="block">
                {materialAnular?.refmaterial}
              </MDTypography>

              <MDTypography variant="body2" color="text" sx={{ overflowWrap: "anywhere" }}>
                {materialAnular?.material}
              </MDTypography>
            </MDBox>

            <MDTypography variant="body2">
              Quantidade atualmente contada:{" "}
              <strong>{materialAnular?.qtd_verificada ?? "—"}</strong>
            </MDTypography>

            <TextField
              label="Motivo da anulação"
              value={motivoAnulacao}
              onChange={(e) => setMotivoAnulacao(e.target.value)}
              multiline
              minRows={2}
              required
              fullWidth
              autoFocus
              disabled={anularLoading}
              helperText="O motivo ficará registado no histórico da contagem."
            />

            <MDTypography variant="caption" color="error" fontWeight="bold">
              Esta operação não altera o stock oficial e não pode ser desfeita diretamente.
            </MDTypography>
          </MDBox>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5, gap: 1, flexWrap: "wrap" }}>
          <MDButton
            color="secondary"
            variant="outlined"
            disabled={anularLoading}
            onClick={() => {
              setMaterialAnular(null);
              setMotivoAnulacao("");
            }}
          >
            Cancelar
          </MDButton>

          <MDButton
            color="error"
            disabled={anularLoading || !motivoAnulacao.trim()}
            onClick={anularContagensMaterial}
          >
            {anularLoading ? "A anular..." : "Confirmar anulação"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(materialContagem) && !confirmacaoContagem}
        onClose={fecharContagem}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            mx: { xs: 1, sm: 2 },
            width: { xs: "calc(100% - 16px)", sm: "100%" },
            maxHeight: "calc(100dvh - 32px)",
          },
        }}
      >
        <DialogTitle>Contagem de material</DialogTitle>

        <DialogContent dividers>
          {materialContagem && (
            <MDBox display="grid" gap={2}>
              <MDBox>
                <MDTypography variant="caption" color="text" display="block">
                  Referência
                </MDTypography>

                <MDTypography variant="h6">{materialContagem.refmaterial}</MDTypography>
              </MDBox>

              <MDBox>
                <MDTypography variant="caption" color="text" display="block">
                  Material
                </MDTypography>

                <MDTypography variant="body2" fontWeight="medium" sx={{ overflowWrap: "anywhere" }}>
                  {materialContagem.material}
                </MDTypography>
              </MDBox>

              <MDBox>
                <MDTypography variant="caption" color="text" display="block">
                  Unidade
                </MDTypography>

                <MDTypography variant="body2">{materialContagem.unidade || "—"}</MDTypography>
              </MDBox>

              {conversaoEANContagem && (
                <MDBox p={1.5} borderRadius="8px" sx={{ backgroundColor: "#eef4ff" }}>
                  <MDTypography variant="caption" color="text" display="block">
                    Código EAN lido
                  </MDTypography>

                  <MDTypography variant="button" fontWeight="bold" display="block">
                    {conversaoEANContagem.codigo}
                  </MDTypography>

                  <MDTypography variant="body2" mt={1}>
                    1 unidade lida equivale a{" "}
                    <strong>
                      {conversaoEANContagem.fator.toLocaleString("pt-PT", {
                        maximumFractionDigits: 12,
                      })}{" "}
                      {materialContagem.unidade || ""}
                    </strong>
                  </MDTypography>

                  {conversaoEANContagem.quantidadeConvertida !== null && (
                    <MDBox
                      mt={2}
                      p={2}
                      sx={{
                        border: "2px solid #2e7d32",
                        borderRadius: "10px",
                        backgroundColor: "#e8f5e9",
                        textAlign: "center",
                      }}
                    >
                      <MDTypography
                        variant="button"
                        fontWeight="bold"
                        sx={{ color: "#1b5e20", display: "block" }}
                      >
                        QUANTIDADE FINAL A REGISTAR NO INVENTÁRIO
                      </MDTypography>

                      <MDTypography variant="h4" fontWeight="bold" sx={{ color: "#1b5e20", mt: 1 }}>
                        {conversaoEANContagem.quantidadeConvertida.toLocaleString("pt-PT", {
                          maximumFractionDigits: 12,
                        })}{" "}
                        {materialContagem.unidade || ""}
                      </MDTypography>

                      <MDBox mt={1.5} pt={1.5} sx={{ borderTop: "1px solid #a5d6a7" }}>
                        <MDTypography
                          variant="button"
                          fontWeight="bold"
                          sx={{ color: "#1b5e20", display: "block" }}
                        >
                          Stock oficial
                        </MDTypography>

                        <MDTypography
                          variant="h5"
                          fontWeight="bold"
                          sx={{ color: "#1b5e20", mt: 0.5 }}
                        >
                          {materialContagem.qtd_esperada !== null &&
                          materialContagem.qtd_esperada !== undefined
                            ? `${Number(materialContagem.qtd_esperada).toLocaleString("pt-PT", {
                                maximumFractionDigits: 12,
                              })} ${materialContagem.unidade || ""}`
                            : "Não disponível"}
                        </MDTypography>
                      </MDBox>

                      <MDTypography
                        variant="caption"
                        sx={{ color: "#2e7d32", display: "block", mt: 1.5 }}
                      >
                        Confirma que esta é a quantidade pretendida antes de guardar.
                      </MDTypography>
                    </MDBox>
                  )}
                </MDBox>
              )}

              <TextField
                label="Quantidade contada"
                type="number"
                value={quantidadeContagem}
                onChange={(e) => setQuantidadeContagem(e.target.value)}
                fullWidth
                required
                autoFocus
                inputProps={{ min: 0, step: "any" }}
                helperText="Introduz a quantidade física encontrada. Zero é uma contagem válida."
              />
            </MDBox>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2,
            py: 1.5,
            gap: 1,
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          {associacaoEANContagem && (
            <MDButton
              color="info"
              variant="outlined"
              onClick={alterarAssociacaoEAN}
              disabled={guardarLoading}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Alterar associação
            </MDButton>
          )}

          <MDButton
            color="secondary"
            variant="outlined"
            onClick={fecharContagem}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Fechar
          </MDButton>

          <MDButton
            color="success"
            disabled={guardarLoading}
            onClick={guardarContagem}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {guardarLoading ? "A guardar..." : "Guardar contagem"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(confirmacaoContagem)}
        onClose={() => {
          if (!guardarLoading) {
            setConfirmacaoContagem(null);
            setTipoConfirmacao("");
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            mx: { xs: 1, sm: 2 },
            width: { xs: "calc(100% - 16px)", sm: "100%" },
            maxHeight: "calc(100dvh - 24px)",
          },
        }}
      >
        <DialogTitle>Confirmar contagem existente</DialogTitle>

        <DialogContent dividers>
          {confirmacaoContagem && (
            <MDBox display="grid" gap={2}>
              <MDTypography variant="body2" fontWeight="bold">
                {materialContagem?.refmaterial} — {materialContagem?.material}
              </MDTypography>

              <MDTypography variant="body2" color="text">
                Já existe uma contagem deste material. Pretende substituir a quantidade anterior,
                acrescentar a nova quantidade ou efetuar uma recontagem completa?
              </MDTypography>

              <MDBox
                display="grid"
                gap={2}
                sx={{
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                }}
              >
                <Card variant="outlined">
                  <MDBox p={2}>
                    <MDTypography variant="caption" color="text">
                      Quantidade anterior
                    </MDTypography>
                    <MDTypography variant="h5">
                      {confirmacaoContagem.anterior.quantidade_final}
                    </MDTypography>
                  </MDBox>
                </Card>

                <Card variant="outlined">
                  <MDBox p={2}>
                    <MDTypography variant="caption" color="text">
                      Nova quantidade
                    </MDTypography>

                    <MDTypography variant="h5">
                      {formatarQuantidadeStock(
                        converterQuantidadeParaStock(confirmacaoContagem.quantidadeNova)
                      )}{" "}
                      {materialContagem?.unidade || ""}
                    </MDTypography>

                    {associacaoEANContagem && (
                      <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                        {formatarQuantidadeStock(confirmacaoContagem.quantidadeNova)} unidade(s)
                        lida(s) pelo EAN {conversaoEANContagem?.codigo}
                      </MDTypography>
                    )}
                  </MDBox>
                </Card>
              </MDBox>

              <RadioGroup
                value={tipoConfirmacao}
                onChange={(e) => setTipoConfirmacao(e.target.value)}
              >
                <FormControlLabel
                  value="SUBSTITUIR"
                  control={<Radio />}
                  label={`Substituir — Total final: ${formatarQuantidadeStock(
                    converterQuantidadeParaStock(confirmacaoContagem.quantidadeNova)
                  )} ${materialContagem?.unidade || ""}`}
                />

                <FormControlLabel
                  value="ACRESCENTAR"
                  control={<Radio />}
                  label={`Acrescentar — Total final: ${formatarQuantidadeStock(
                    Number(confirmacaoContagem.anterior.quantidade_final) +
                      converterQuantidadeParaStock(confirmacaoContagem.quantidadeNova)
                  )} ${materialContagem?.unidade || ""}`}
                />

                <FormControlLabel
                  value="RECONTAR"
                  control={<Radio />}
                  label="Recontagem completa — Indicar novo total"
                />
              </RadioGroup>

              {tipoConfirmacao === "RECONTAR" && (
                <TextField
                  label="Quantidade total recontada"
                  type="number"
                  value={quantidadeRecontagem}
                  onChange={(e) => setQuantidadeRecontagem(e.target.value)}
                  inputProps={{ min: 0, step: "any" }}
                  helperText="Indica o total físico verificado nesta recontagem."
                  fullWidth
                  required
                />
              )}

              {tipoConfirmacao && (
                <MDBox
                  p={2}
                  sx={{
                    borderRadius: 2,
                    bgcolor: "background.default",
                  }}
                >
                  <MDTypography variant="caption" color="text">
                    Quantidade final resultante
                  </MDTypography>

                  <MDTypography variant="h5" fontWeight="bold">
                    {tipoConfirmacao === "SUBSTITUIR"
                      ? formatarQuantidadeStock(
                          converterQuantidadeParaStock(confirmacaoContagem.quantidadeNova)
                        )
                      : tipoConfirmacao === "ACRESCENTAR"
                      ? formatarQuantidadeStock(
                          Number(confirmacaoContagem.anterior.quantidade_final) +
                            converterQuantidadeParaStock(confirmacaoContagem.quantidadeNova)
                        )
                      : quantidadeRecontagem !== "" && Number.isFinite(Number(quantidadeRecontagem))
                      ? formatarQuantidadeStock(converterQuantidadeParaStock(quantidadeRecontagem))
                      : "—"}{" "}
                    {materialContagem?.unidade || ""}
                  </MDTypography>
                </MDBox>
              )}

              <TextField
                label="Observações (opcional)"
                value={observacoesConfirmacao}
                onChange={(e) => setObservacoesConfirmacao(e.target.value)}
                multiline
                minRows={2}
                fullWidth
              />
            </MDBox>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2,
            py: 1.5,
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <MDButton
            color="secondary"
            variant="outlined"
            disabled={guardarLoading}
            onClick={() => {
              setConfirmacaoContagem(null);
              setTipoConfirmacao("");
            }}
          >
            Voltar
          </MDButton>

          <MDButton
            color="success"
            disabled={!tipoConfirmacao || guardarLoading}
            onClick={confirmarContagemRepetida}
          >
            {guardarLoading ? "A guardar..." : "Confirmar contagem"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {inventario && !inventario.dataencerramento && (
        <>
          <MDBox
            sx={{
              position: "fixed",
              bottom: {
                xs: "calc(20px + env(safe-area-inset-bottom))",
                md: 28,
              },
              right: {
                xs: "calc(16px + env(safe-area-inset-right))",
                md: 28,
              },
              zIndex: 1200,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "flex-end",
              gap: 1,
              maxWidth: "calc(100vw - 32px)",
            }}
          >
            <Tooltip title="Ler códigos através da câmera" placement="top">
              <Fab
                color="info"
                variant="extended"
                onClick={() => {
                  setUsarCameraBarcode(true);
                  setOpenBarcodeReader(true);
                }}
                sx={{
                  minHeight: 52,
                  px: 2,
                  boxShadow: 6,
                  whiteSpace: "nowrap",
                }}
              >
                <PhotoCameraIcon sx={{ mr: 1 }} />
                Usar Câmara
              </Fab>
            </Tooltip>

            <Tooltip title="Ler códigos com um leitor Barcode físico" placement="top">
              <Fab
                color="dark"
                variant="extended"
                onClick={() => {
                  setUsarCameraBarcode(false);
                  setOpenBarcodeReader(true);
                }}
                sx={{
                  minHeight: 52,
                  px: 2,
                  boxShadow: 6,
                  whiteSpace: "nowrap",
                  color: "#ffffff",
                  "& .MuiSvgIcon-root": {
                    color: "#ffffff",
                  },
                }}
              >
                <DocumentScannerIcon sx={{ mr: 1 }} />
                Usar Leitor Barcode
              </Fab>
            </Tooltip>
          </MDBox>

          <DialogBarcodeReader
            open={openBarcodeReader}
            onClose={() => {
              setOpenBarcodeReader(false);
              setCodigoPendenteEAN(null);
              setQuantidadePendenteEAN(null);
            }}
            onMaterialDetected={selecionarMaterialPorCodigo}
            permitirAssociacoes
            usarCamera={usarCameraBarcode}
            codigoInicial={codigoPendenteEAN}
            selecionarExplicitamente={Boolean(codigoPendenteEAN)}
          />
        </>
      )}
    </DashboardLayout>
  );
}
